/**
 * CustomerTopNav — Phase 2 customer-shell header, Phase 3 polished.
 *
 * Desktop row (≥ md):
 *   [PooPluckers logo]
 *     [Dashboard][My Plucks][My dogs][Billing]   (OR: Step N/4 progress)
 *       [orange "Book a Pluck" CTA] [avatar chip]
 *
 * Mobile (< md):
 *   [hamburger] [logo]    [CTA] [avatar chip]
 *   The 4 nav links collapse into the hamburger Sheet. During the
 *   booking wizard, the hamburger is hidden (no nav-link temptation)
 *   and a compact "Step N of 4" label appears next to the logo so
 *   users still see progress at a glance.
 *
 * Wizard awareness (Phase 3 §4): consumes `useBookingWizard()`. If
 * mounted, the desktop center renders `<BookingWizardProgress>` and
 * the mobile hamburger is suppressed. Marketing surfaces ("Plans &
 * pricing", "About & help") remain demoted into the AvatarDropdown.
 */
import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarCheck,
  PawPrint,
  Wallet,
  DollarSign,
  HelpCircle,
  Settings,
  Menu,
} from "lucide-react";
import AvatarDropdown from "@/components/AvatarDropdown";
import ThemeToggle from "@/components/ThemeToggle";
import BookingWizardProgress from "@/components/customer/BookingWizardProgress";
import { useBookingWizard } from "@/context/BookingWizardContext";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", to: "/dashboard/customer", icon: LayoutDashboard, exact: true },
  { id: "bookings",  label: "My Plucks", to: "/dashboard/customer/bookings", icon: CalendarCheck },
  { id: "dogs",      label: "My dogs",   to: "/dashboard/customer/dogs", icon: PawPrint },
  { id: "billing",   label: "Billing",   to: "/dashboard/customer/billing", icon: Wallet },
];

// Demoted marketing items — passed to AvatarDropdown.
const CUSTOMER_EXTRA_MENU_ITEMS = [
  { key: "settings",      label: "Settings",        to: "/dashboard/customer/settings", icon: Settings },
  { key: "plans-pricing", label: "Plans & pricing", to: "/pricing", icon: DollarSign },
  { key: "about-help",    label: "About & help",    to: "/about",   icon: HelpCircle },
];

function CustomerLogo() {
  return (
    <Link
      to="/dashboard/customer"
      className="inline-flex items-center gap-2 select-none"
      data-testid="customer-shell-logo"
      aria-label="PooPluckers customer home"
    >
      <span
        className="text-[22px] sm:text-[24px] leading-none tracking-[-0.02em]"
        style={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800 }}
      >
        <span style={{ color: "var(--pp-orange)" }}>Poo</span>
        <span style={{ color: "var(--pp-slate)" }}>Pluckers</span>
      </span>
    </Link>
  );
}

function CustomerNav({ orientation = "horizontal", onItemClick }) {
  return (
    <nav
      className={cn(
        orientation === "horizontal"
          ? "flex items-center gap-1"
          : "flex flex-col gap-1"
      )}
      aria-label="Customer navigation"
      data-testid={
        orientation === "horizontal"
          ? "customer-shell-nav"
          : "customer-shell-mobile-nav"
      }
    >
      {NAV_ITEMS.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.id}
            to={it.to}
            end={it.exact}
            onClick={onItemClick}
            className={({ isActive }) =>
              cn(
                orientation === "horizontal"
                  ? "inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[14px] transition-colors"
                  : "flex items-center gap-2 rounded-md px-3 py-3 text-[17px] transition-colors",
                isActive
                  ? "font-bold text-pp-orange"
                  : "font-medium text-pp-slate hover:text-pp-orange"
              )
            }
            data-testid={`customer-shell-nav-${it.id}`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span>{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function BookCTA() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate("/book")}
      className="inline-flex h-10 items-center gap-1.5 rounded-full px-3 sm:px-4 text-[13px] sm:text-[14px] font-bold transition-colors hover:opacity-90"
      style={{ backgroundColor: "var(--pp-orange)", color: "var(--pp-cta-text)" }}
      data-testid="customer-shell-cta-book"
    >
      Book a Pluck
    </button>
  );
}

export default function CustomerTopNav() {
  const wizardCtx = useBookingWizard();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const inWizard = !!wizardCtx;

  return (
    <header
      className="border-b border-pp-line bg-pp-v3-bg"
      data-testid="customer-shell-topnav"
      data-wizard-active={inWizard || undefined}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 sm:gap-6 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-2 min-w-0">
          {/* Mobile hamburger — hidden during wizard so users can't
              click away mid-flow. */}
          {!inWizard && (
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="md:hidden inline-flex items-center justify-center rounded-md text-pp-slate"
                  style={{ width: 44, height: 44 }}
                  aria-label="Open customer menu"
                  data-testid="customer-shell-hamburger-btn"
                >
                  <Menu size={24} aria-hidden="true" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[280px] bg-pp-v3-bg"
                data-testid="customer-shell-mobile-sheet"
              >
                <div className="pt-2 pb-4">
                  <CustomerLogo />
                </div>
                <CustomerNav
                  orientation="vertical"
                  onItemClick={() => setDrawerOpen(false)}
                />
              </SheetContent>
            </Sheet>
          )}
          <CustomerLogo />
          {/* Mobile-only compact wizard label, surfaced when no
              hamburger is rendered. Desktop renders the full progress
              bar in the center slot below. */}
          {inWizard && (
            <span
              className="md:hidden ml-2 text-[12px] font-bold uppercase tracking-[0.08em] text-pp-text-soft"
              data-testid="customer-shell-mobile-wizard-label"
            >
              Step {wizardCtx.step} of {wizardCtx.totalSteps}
            </span>
          )}
        </div>

        {/* Center slot — desktop only. Either nav links OR wizard
            progress, never both. */}
        <div className="hidden md:flex flex-1 justify-center">
          {inWizard ? (
            <BookingWizardProgress
              step={wizardCtx.step}
              totalSteps={wizardCtx.totalSteps}
            />
          ) : (
            <CustomerNav />
          )}
        </div>

        <div className="flex items-center gap-2">
          <BookCTA />
          <ThemeToggle />
          <AvatarDropdown extraMenuItems={CUSTOMER_EXTRA_MENU_ITEMS} />
        </div>
      </div>
    </header>
  );
}
